import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Drawer,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import {
  DataTable,
  DrawerHead,
  EmptyState,
  Figure,
  Figures,
  Headline,
  Legend,
  Loading,
  Proportion,
  Provenance,
  Refusal,
  StackedBars,
  Tooltip,
} from "@lopie/kernel-ui";

import { parseHash } from "../hash.js";
import {
  VOCABULARY_PROBE,
  auditParams,
  crowdedNotice,
  lensHash,
  lensRecord,
  lensStorageKey,
  readLens,
  waitingFor,
} from "./activityLens.js";

const PAGE_SIZES = [25, 50, 100, 200];

/** This tab's session storage, or null where the browser will not give it out. */
function tabStorage() {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * What happened, and who did it.
 *
 * This section was a placeholder from the day the console shipped, and the
 * placeholder said why: `GET /audit` did not exist, and the reason it did not
 * exist was LO-64 — the audit log is unrewritable today only because the API
 * role has no read on that table. Building this meant granting that read
 * somewhere, and the only safe somewhere was a second role.
 *
 * So the screen has an obligation the other seven do not. It is displaying a
 * guarantee, and it must not overstate it. Everything under "What this does
 * not prove" is the kernel's own words, rendered rather than summarised,
 * including the two sentences a reader is most likely to assume the opposite
 * of: that reading this log is not itself recorded, and that overwrite is
 * prevented by a *credential* rather than by cryptography.
 *
 * ## Rendered from the kernel
 *
 * Every control on this page is built from the `vocabulary` block the
 * endpoint returns: the two modes, the transports the kernel will attest,
 * every action it can write, every agent every app declares, and the sentence
 * for each kind of subject. There is no list of verbs in this file and no map
 * of subject kinds — a verb, a transport or an agent added to the kernel
 * appears here with no change to this screen, which is the rule `/schema`
 * sets for columns and this is the same rule one layer up.
 *
 * ## Control-plane rows are not an afterthought
 *
 * `ACCESS#<person>` and `ACCESS#manifest:<app>` are the most
 * security-relevant rows in the table and a screen that only understood
 * business objects would render them as blank. They are a first-class subject
 * kind here: chipped by kind in the table, explained by the kernel's own
 * sentence, and their `detail` — a grant's groups before and after, an
 * approval's trimmed ceiling — rendered generically, so a control-plane write
 * that starts recording something new shows up with no change to this file.
 */
export default function Activity({ get }) {
  // Where the lens starts: the address if it names one, else what this tab
  // last had, else Everything. Read once. It used to start as Everything every
  // time, so a reload threw away the lens somebody had just chosen.
  const [opened] = useState(() =>
    readLens({ hash: window.location.hash, storage: tabStorage() }));
  const [lens, setLens] = useState(opened.lens);
  // What the kernel is asked about, and what is typed in the box. The second
  // becomes the first once typing pauses (or on Enter), so a person or a
  // manifest:app is not looked up once per character on the way to being typed.
  const [qualifier, setQualifier] = useState(opened.qualifier);
  const [draft, setDraft] = useState(opened.qualifier);
  const [filters, setFilters] = useState({
    mode: "", agent: "", actor: "", on_behalf_of: "", via: "", app_type: "",
  });
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  // Which button is lit. Derived state would be cleaner and is wrong here: a
  // person who types a date into `From` has not chosen "7 days", and lighting
  // the button whose range happens to match would claim they did.
  const [span, setSpan] = useState("");
  const [sort, setSort] = useState("desc");
  const [limit, setLimit] = useState(50);

  const [page, setPage] = useState(null);
  const [stack, setStack] = useState([]); // cursors behind us
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  // Whether the first read has finished. Only the first one replaces the screen
  // with a loading state: doing it on every read unmounted the filter boxes
  // whenever there was no page to show, which took the focus out of the box
  // somebody was typing a person into after every character.
  const [read, setRead] = useState(false);
  const [failure, setFailure] = useState(null);
  // What the kernel can be asked, kept from the last page that carried it. A
  // lens that is waiting for a value has no page, and the Action list and the
  // other selects are built from this.
  const [vocabulary, setVocabulary] = useState(null);
  const known = useRef(null);
  const asked = useRef(0);
  const [selected, setSelected] = useState(null);
  // When the rows on screen were read, and a clock to say it against. A log
  // with no read time is a log you cannot tell from a frozen one.
  const [readAt, setReadAt] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const [live, setLive] = useState(true);

  // Reset paging whenever the *query* changes rather than only when a control
  // is touched. A cursor belongs to one lens and one direction — the kernel
  // refuses one from another, by design — and carrying it across a change
  // would turn a filter click into a 400 the reader did not cause.
  const reset = (fn) => (event) => {
    setStack([]);
    setCursor(null);
    fn(event.target.value);
  };
  const setFilter = (name) => reset((value) =>
    setFilters((current) => ({ ...current, [name]: value })));

  // A lens that needs a value and has none is not asked. It used to fall
  // through to the Everything query, which is the question the reader had just
  // chosen not to ask, and which was the one that failed on a busy log.
  const waiting = waitingFor(lens, qualifier);

  const load = useCallback(async () => {
    // Every read takes a number, and only the latest may write to the screen:
    // an Everything read still in flight when the lens changes would otherwise
    // land on top of a lens that is waiting for a person.
    const mine = ++asked.current;
    const params = auditParams({
      lens, qualifier, filters, limit, sort, since, until, cursor,
    });

    if (!params) {
      setPage(null);
      setFailure(null);
      setLoading(false);
      setRead(true);
      if (!known.current) {
        // The vocabulary arrives on a page and there is no page to wait for,
        // so ask the one question whose answer is nothing: a window that ended
        // before the log began. It is read for its vocabulary and discarded.
        get("/audit", VOCABULARY_PROBE)
          .then((body) => {
            if (body?.vocabulary && !known.current) {
              known.current = body.vocabulary;
              setVocabulary(body.vocabulary);
            }
          })
          .catch(() => {});
      }
      return;
    }

    setLoading(true);
    setFailure(null);
    try {
      const body = await get("/audit", params);
      if (mine !== asked.current) return;
      if (body?.vocabulary) {
        known.current = body.vocabulary;
        setVocabulary(body.vocabulary);
      }
      setPage(body);
      setReadAt(Date.now());
    } catch (e) {
      if (mine !== asked.current) return;
      setPage(null);
      setFailure(e);
    } finally {
      if (mine === asked.current) {
        setLoading(false);
        setRead(true);
      }
    }
  }, [get, limit, sort, since, until, cursor, lens, qualifier, filters]);

  useEffect(() => {
    load();
  }, [load]);

  // Typing pauses, then the box's value is what the kernel is asked about.
  useEffect(() => {
    if (draft === qualifier) return undefined;
    const timer = setTimeout(() => {
      setStack([]);
      setCursor(null);
      setQualifier(draft);
    }, 500);
    return () => clearTimeout(timer);
  }, [draft, qualifier]);

  // Keep the lens across a reload: in the address, where a copied link opens
  // the same view, and in this tab's storage for the cases the address does
  // not survive. Neither is a place anything is decided from; both are read
  // once, on the way in.
  useEffect(() => {
    try {
      const { pathname, search, hash } = window.location;
      const section = parseHash(hash).id || "activity";
      window.history.replaceState(
        null, "", `${pathname}${search}${lensHash(section, lens, qualifier)}`);
    } catch {
      // A browser that will not rewrite the address has not lost anything else.
    }
    try {
      tabStorage()?.setItem(lensStorageKey, lensRecord(lens, qualifier));
    } catch {
      // Storage full or blocked: the lens simply is not remembered.
    }
  }, [lens, qualifier]);

  // The latest `load`, without making the timer depend on it. `load` is a new
  // function every time a filter changes, and an interval that listed it as a
  // dependency would be torn down and rebuilt on every keystroke in the actor
  // box — which is a re-read nobody asked for, on a screen that prints what
  // each read cost.
  const latest = useRef(load);
  latest.current = load;

  /**
   * One timer, two jobs: move the clock, and re-read when it is safe to.
   *
   * ## Why it does not re-read while you are paging
   *
   * A cursor names a position in one particular walk. Re-reading page four
   * every thirty seconds would either hand back the same four pages behind
   * you or quietly shift what "page four" means as rows land at the front —
   * and a log that reorders itself under a reader is worse than a stale one.
   *
   * So it is live on the first page and stops the moment you page in, which
   * is said on the control rather than left to be noticed.
   *
   * ## Why it can be turned off
   *
   * Every read costs, and this screen prints what — `306 rows examined to
   * return 50`. A screen that spent that every thirty seconds forever without
   * a way to stop it would be one people close rather than leave open.
   */
  useEffect(() => {
    const paging = stack.length > 0 || Boolean(cursor);
    const tick = setInterval(() => {
      setNow(Date.now());
      if (!live || paging || document.hidden) return;
      if (Date.now() - (readAt || 0) >= 30000) latest.current();
    }, 5000);
    return () => clearInterval(tick);
  }, [live, stack.length, cursor, readAt]);

  // The palette for the chart, read off the theme rather than written here.
  // Teal steps plus a neutral: nothing in this product is ever teal because
  // it is *permitted*, so nothing teal can be misread as a state.
  const theme = useTheme();
  const palette = theme.palette.series || [theme.palette.primary.main];

  /**
   * What this page looks like, bucketed.
   *
   * ## Why the bucket size is chosen rather than fixed
   *
   * A page can be four hours of a busy night or three weeks of a quiet one,
   * and one bucket size cannot serve both: hourly over three weeks is five
   * hundred bars, daily over four hours is one. So the span decides — under
   * two days is hourly, past that is daily — and the caption says which,
   * because a reader comparing two pages needs to know the axis changed.
   *
   * ## Who counts as a writer
   *
   * `app_type` when the envelope has one and `actor` when it does not, which
   * is the same rule the Who column follows. A connector holds no manifest,
   * so `app_type` is absent rather than missing and its actor already reads
   * as a name. Nothing is inferred: a row with neither is bucketed under its
   * actor exactly as it stands.
   */
  const shape = useMemo(() => {
    const items = page?.items || [];
    const empty = {
      buckets: [], writers: [], byWriter: {}, detail: {}, kinds: {},
      unit: "daily", tallest: 0, actions: 0, unattendedShare: 0,
    };
    if (items.length < 2) return empty;

    const nameOf = (row) => {
      const p = row.provenance || {};
      const actor = String(p.actor || "");
      if (p.app_type) return p.app_type;
      if (actor.startsWith("connector:")) return actor.slice("connector:".length);
      return actor || "(no actor recorded)";
    };

    const times = items
      .map((row) => Date.parse(row.at))
      .filter((n) => !Number.isNaN(n));
    if (times.length < 2) return empty;

    const first = Math.min(...times);
    const last = Math.max(...times);
    const hourly = last - first < 48 * 3600 * 1000;
    const size = hourly ? 3600 * 1000 : 24 * 3600 * 1000;
    const key = (ms) => {
      const d = new Date(Math.floor(ms / size) * size);
      return hourly
        ? d.toISOString().slice(0, 13).replace("T", " ") + ":00"
        : d.toISOString().slice(0, 10);
    };

    const byWriter = {};
    const kinds = {};
    const actions = new Set();
    const cells = new Map();
    // Per writer: how the work split by mode, and which verbs it used. The
    // mode split is three-way and never two — a row written before the field
    // existed is its own case, and folding it into `interactive` would invent
    // the one fact `mode` was added to stop inferring.
    const detail = {};
    let unattended = 0;
    let moded = 0;

    for (const row of items) {
      const at = Date.parse(row.at);
      if (Number.isNaN(at)) continue;
      const who = nameOf(row);
      const bucket = key(at);
      byWriter[who] = (byWriter[who] || 0) + 1;
      kinds[who] = row.provenance?.app_type ? "app" : "connector";
      if (row.provenance?.action) actions.add(row.provenance.action);

      if (!detail[who]) {
        detail[who] = {
          count: 0, unattended: 0, interactive: 0, unrecorded: 0, verbs: {},
        };
      }
      const mine = detail[who];
      mine.count += 1;
      const verb = row.provenance?.action;
      if (verb) mine.verbs[verb] = (mine.verbs[verb] || 0) + 1;

      const mode = row.provenance?.mode;
      if (mode === "unattended") {
        mine.unattended += 1;
        moded += 1;
        unattended += 1;
      } else if (mode === "interactive") {
        mine.interactive += 1;
        moded += 1;
      } else {
        mine.unrecorded += 1;
      }
      if (!cells.has(bucket)) cells.set(bucket, {});
      const cell = cells.get(bucket);
      cell[who] = (cell[who] || 0) + 1;
    }

    // Every bucket between the first and the last, not only the ones with
    // rows — otherwise four writes on Monday and four on Friday draw as two
    // equal bars side by side and read as two busy days in a row.
    const buckets = [];
    for (let ms = Math.floor(first / size) * size; ms <= last; ms += size) {
      const label = key(ms);
      buckets.push({ label, values: cells.get(label) || {} });
      // Past this it stops being a shape and starts being a bad bar chart.
      if (buckets.length > 90) break;
    }

    // Busiest first, so the biggest band is at the bottom of every column and
    // the eye has a flat baseline to read the rest against.
    const writers = Object.keys(byWriter).sort(
      (a, b) => byWriter[b] - byWriter[a],
    );

    const tallest = Math.max(
      ...buckets.map((b) => Object.values(b.values).reduce((n, v) => n + v, 0)),
      0,
    );

    // Verbs as a sorted list, most used first, so the chip that matters is
    // the one a reader sees before the row wraps.
    for (const who of Object.keys(detail)) {
      detail[who].actions = Object.entries(detail[who].verbs)
        .sort((a, b) => b[1] - a[1]);
    }

    return {
      buckets,
      writers,
      byWriter,
      detail,
      kinds,
      unit: hourly ? "hourly" : "daily",
      tallest,
      actions: actions.size,
      // Of the rows that carry a mode at all. Rows written before the field
      // existed are in neither half and dividing by every row would quietly
      // count them as attended.
      unattendedShare: moded ? Math.round((unattended / moded) * 100) : 0,
    };
  }, [page]);

  const agents = vocabulary?.agents || [];
  const chosenAgent = agents.find((a) => a.name === filters.agent);

  const columns = useMemo(() => [
    {
      key: "at",
      label: "When",
      mono: true,
      width: 210,
      sx: { whiteSpace: "nowrap" },
      render: (row) => when(row.at),
    },
    {
      key: "subject",
      label: "What it touched",
      render: (row) => <Subject subject={row.subject} />,
    },
    {
      key: "action",
      label: "Action",
      mono: true,
      render: (row) => row.provenance?.action || "—",
    },
    {
      key: "actor",
      label: "Who",
      render: (row) => <Who p={row.provenance} />,
    },
    {
      key: "mode",
      label: "Mode",
      render: (row) => <Mode mode={row.provenance?.mode} />,
    },
    {
      key: "agent",
      label: "Agent",
      mono: true,
      render: (row) => row.provenance?.agent || "—",
    },
  ], []);

  if (!read && loading) return <Loading label="Reading the audit log" />;

  const crowded = crowdedNotice(page);

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ alignItems: "flex-start", flexWrap: "wrap", rowGap: 1.5 }}
        >
          <TextField
            select
            size="small"
            label="Lens"
            value={lens}
            onChange={reset((value) => {
              // The new lens has no value yet, so nothing is asked until it
              // has one (see `waiting`).
              setLens(value);
              setQualifier("");
              setDraft("");
            })}
            sx={{ minWidth: 190 }}
            helperText={LENSES[lens]}
          >
            {Object.entries(LENSES).map(([id, help]) => (
              <MenuItem key={id} value={id}>
                {LENS_LABELS[id]}
              </MenuItem>
            ))}
          </TextField>

          {lens === "action" ? (
            <TextField
              select
              size="small"
              label="Action"
              value={qualifier}
              onChange={reset((value) => {
                setQualifier(value);
                setDraft(value);
              })}
              sx={{ minWidth: 230 }}
              helperText={qualifier ? undefined : "Choose one to read"}
            >
              {(vocabulary?.actions || []).map((a) => (
                <MenuItem key={a} value={a}>
                  {a}
                </MenuItem>
              ))}
            </TextField>
          ) : null}

          {lens === "subject" || lens === "record" ? (
            <TextField
              size="small"
              label={lens === "record" ? "Type/id" : "Person or manifest:app"}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                // Enter does not wait out the pause.
                if (event.key !== "Enter" || draft === qualifier) return;
                setStack([]);
                setCursor(null);
                setQualifier(draft);
              }}
              autoFocus={!draft}
              sx={{ minWidth: 260 }}
              placeholder={
                lens === "record"
                  ? "Organization/01M2…"
                  : "josh@lopie.dev or manifest:crowcase"
              }
            />
          ) : null}

          <TextField
            select
            size="small"
            label="Mode"
            value={filters.mode}
            onChange={setFilter("mode")}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">Either</MenuItem>
            {(vocabulary?.modes || []).map((m) => (
              <MenuItem key={m} value={m}>
                {m}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Agent"
            value={filters.agent}
            onChange={setFilter("agent")}
            sx={{ minWidth: 190 }}
          >
            <MenuItem value="">Any, or none</MenuItem>
            {agents.map((a) => (
              <MenuItem key={`${a.app_type}/${a.name}`} value={a.name}>
                {a.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Came through"
            value={filters.via}
            onChange={setFilter("via")}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">Any</MenuItem>
            {(vocabulary?.transports || []).map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            size="small"
            label="Actor"
            value={filters.actor}
            onChange={setFilter("actor")}
            sx={{ minWidth: 200 }}
            placeholder="connector:xero"
          />

          <TextField
            size="small"
            label="On behalf of"
            value={filters.on_behalf_of}
            onChange={setFilter("on_behalf_of")}
            sx={{ minWidth: 200 }}
            placeholder="user:josh@lopie.dev"
          />

          <TextField
            size="small"
            label="From"
            value={since}
            onChange={reset(setSince)}
            sx={{ minWidth: 150 }}
            placeholder="2026-09"
            helperText="ISO prefix"
          />

          <TextField
            size="small"
            label="To"
            value={until}
            onChange={reset(setUntil)}
            sx={{ minWidth: 150 }}
            placeholder="2026-09-08"
            helperText="inclusive"
          />

          <TextField
            select
            size="small"
            label="Order"
            value={sort}
            onChange={reset(setSort)}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="desc">Newest first</MenuItem>
            <MenuItem value="asc">Oldest first</MenuItem>
          </TextField>

          <TextField
            select
            size="small"
            label="Per page"
            value={limit}
            onChange={reset((v) => setLimit(Number(v)))}
            sx={{ minWidth: 110 }}
          >
            {PAGE_SIZES.map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ flex: 1, minWidth: 0 }} />

          <Button
            size="small"
            disabled={!stack.length || loading}
            onClick={() => {
              const back = [...stack];
              setCursor(back.pop() ?? null);
              setStack(back);
            }}
          >
            Back
          </Button>
          <Button
            size="small"
            disabled={!page?.cursor || loading}
            onClick={() => {
              setStack([...stack, cursor]);
              setCursor(page.cursor);
            }}
          >
            Next
          </Button>
        </Stack>

        {chosenAgent ? (
          // The Agent OS question, answered where it is asked. An audit row
          // naming an agent nobody has heard of is a name; the declaration
          // beside it is what makes it an explanation, and it comes from the
          // app's own manifest rather than from anything written here.
          <Alert severity="info" variant="outlined" sx={{ mt: 1.5 }}>
            <AlertTitle>
              {chosenAgent.name} — {chosenAgent.division}, of{" "}
              {chosenAgent.app_type}
            </AlertTitle>
            {chosenAgent.does} It acts inside {chosenAgent.app_type}&apos;s
            ceiling and holds no permission of its own, so everything below is
            a write that app was already allowed to make.
          </Alert>
        ) : null}
      </Paper>

      {failure ? (
        <Refusal failure={failure} actor="This console" onRetry={load} />
      ) : null}

      {page ? (
        <Headline
          eyebrow={`On this page${page.has_more ? ", of more" : ""}`}
          figure={Number(page.count || 0).toLocaleString()}
          caption={`write${page.count === 1 ? "" : "s"}${
            since ? " in this window" : ", newest first"
          }`}
          basis={`${page.cost} A page is what one read returned, not what the log holds — there is no total anywhere in this product, because a total is a full read of a table whose whole job is to grow.`}
          right={
            <Stack spacing={1.25} sx={{ alignItems: { xs: "flex-start", sm: "flex-end" } }}>
              <Windows
                value={span}
                onChange={(next, from) => {
                  setStack([]);
                  setCursor(null);
                  setSpan(next);
                  setSince(from);
                }}
              />
              <Freshness
                readAt={readAt}
                now={now}
                live={live}
                loading={loading}
                paging={stack.length > 0 || Boolean(cursor)}
                onToggle={() => setLive((was) => !was)}
                onRefresh={() => load()}
              />
            </Stack>
          }
        >
          <Figures sx={{ mt: 2.5 }}>
            <Figure
              value={shape.writers.length.toLocaleString()}
              label="writers"
              basis="Distinct apps, connectors and agents named on this page. An app is named by its app_type and a connector by its actor, because a connector holds no manifest and has no app_type to be named by."
            />
            <Figure
              value={shape.actions.toLocaleString()}
              label="kinds of write"
              basis="Distinct action verbs on this page. A page of one verb repeated and a page of nine different ones are very different days and the row count says the same thing about both."
            />
            <Figure
              value={`${shape.unattendedShare}%`}
              label="with nobody there"
              tone={shape.unattendedShare >= 50 ? "unattended" : undefined}
              basis="mode is stamped by the kernel from the shape of the token and the caller cannot assert it. Rows written before the field existed carry none at all and are counted in neither half."
            />
          </Figures>
        </Headline>
      ) : null}

      {/* ## The shape, and what it is of
          Bucketed from the rows on this page and captioned as such. The
          endpoint bounds its walk, so a chart titled "activity" over whatever
          one read returned would be the overstatement this product exists to
          prevent, in the medium where it is easiest — a chart carries more
          conviction per pixel than a sentence does. */}
      {shape.buckets.length > 1 ? (
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Typography variant="h2">Over time, by writer</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Stacked by writer · {shape.unit} buckets · the {page.count} rows on
            this page
          </Typography>
          <StackedBars
            buckets={shape.buckets}
            series={shape.writers}
            colours={palette}
            caption={`${shape.unit} buckets · tallest holds ${shape.tallest}`}
            label={`Writes per ${shape.unit} across ${shape.buckets.length} buckets, stacked by each of ${shape.writers.length} writers.`}
          />
          {/* Clicking a writer filters the table below it, because a legend
              that names the thing you want and cannot get you there is a
              legend that makes you retype it into the box underneath. */}
          <Legend
            series={shape.writers}
            counts={shape.byWriter}
            colours={palette}
            selected={filters.app_type || filters.actor}
            onSelect={(key) => {
              setStack([]);
              setCursor(null);
              const connector = shape.kinds[key] === "connector";
              setFilters((current) => ({
                ...current,
                app_type: key && !connector ? key : "",
                actor: key && connector ? `connector:${key}` : "",
              }));
            }}
          />
        </Paper>
      ) : null}

      {/* ## Per writer
          The legend says how much each one did. This says *what they did* and
          — the part no other product on this list can show — how much of it
          had nobody in front of it. The proportion bar carries `mode`, which
          is a state and therefore semantic colour; the avatar carries the
          series colour, which is identity. Keeping those two jobs in two
          places is what stops a chart legend from looking like a permission. */}
      {shape.writers.length > 1 ? (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h2">Per writer</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Of the {page.count} rows on this page, busiest first. Click one to
            narrow everything below it.
          </Typography>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(auto-fit, minmax(280px, 1fr))",
              },
              alignItems: "stretch",
            }}
          >
            {shape.writers.map((name, index) => (
              <WriterCard
                key={name}
                name={name}
                colour={palette[index % palette.length]}
                kind={shape.kinds[name]}
                detail={shape.detail[name]}
                total={page.count}
                selected={
                  filters.app_type === name
                  || filters.actor === `connector:${name}`
                }
                onSelect={() => {
                  setStack([]);
                  setCursor(null);
                  const connector = shape.kinds[name] === "connector";
                  const on =
                    filters.app_type === name
                    || filters.actor === `connector:${name}`;
                  setFilters((current) => ({
                    ...current,
                    app_type: !on && !connector ? name : "",
                    actor: !on && connector ? `connector:${name}` : "",
                  }));
                }}
              />
            ))}
          </Box>
        </Box>
      ) : null}

      {crowded ? (
        // A timestamp that more rows share than one read will take is listed
        // in part, and the kernel says which. Drawn as rows with nothing said,
        // this would be an audit log answering "nothing else happened" about
        // the busiest instant in it (LO-232).
        <Alert severity="warning" variant="outlined">
          <AlertTitle>{crowded.title}</AlertTitle>
          {crowded.detail}
          <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
            {crowded.marks.map((m) => (
              <li key={m.key}>
                <Box component="span" sx={{ fontFamily: "monospace", fontSize: "0.8125rem" }}>
                  {m.label}
                </Box>
                {" "}— {m.shown.toLocaleString()} listed
              </li>
            ))}
          </Box>
          {crowded.short ? (
            <Typography variant="body2" sx={{ mt: 1 }}>
              This page is short because it ended at that instant, not because
              the log did. Next carries on past it.
            </Typography>
          ) : null}
        </Alert>
      ) : page?.stopped_early ? (
        // The one thing this screen must never render as "that is
        // everything". A short page with a Next button is a read that ran out
        // of budget, not a log that ran out of rows.
        <Alert severity="warning" variant="outlined">
          <AlertTitle>This page stopped early</AlertTitle>
          {page.stopped_detail}
        </Alert>
      ) : null}

      {waiting && !failure ? (
        // Nothing has been asked, which is not the same as nothing matched.
        <EmptyState title="Waiting for a value">
          <Typography variant="body2">{waiting}</Typography>
        </EmptyState>
      ) : null}

      {page ? (
        <DataTable
          columns={columns}
          rows={page.items}
          getRowKey={(row) => row.event}
          onRowClick={setSelected}
          stickyHeader
          empty={
            <EmptyState title="Nothing matched">
              <Typography variant="body2">
                The read succeeded and found no rows in this window. That is a
                fact about what has been written, not about what you are
                allowed to see — this endpoint is not filtered by your tags,
                and a refusal would have said so.
              </Typography>
            </EmptyState>
          }
          caption={
            <>
              {page.count} on this page
              {page.has_more ? ", more available" : ", last page"} · page{" "}
              {stack.length + 1} · {page.cost}.
            </>
          }
        />
      ) : null}

      <Drawer
        anchor="right"
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        slotProps={{ paper: { sx: { width: { xs: "100%", sm: 560, md: 760 } } } }}
      >
        {selected ? (
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <DrawerHead
              title="One change"
              id={selected.provenance?.action}
              onClose={() => setSelected(null)}
              lede="A single change, in full: what was changed, which app made it, whether a signed-in person was behind it, and when. The kernel writes this record itself at the moment of the change, so no app can leave anything out of it or put something else in."
            />
            <Event row={selected} kinds={vocabulary?.subject_kinds} />
          </Box>
        ) : null}
      </Drawer>
    </Stack>
  );
}

const LENS_LABELS = {
  everything: "Everything",
  record: "One record",
  subject: "One person or app",
  action: "One action",
};

const LENSES = {
  everything: "Every action partition, merged",
  record: "One record's whole history",
  subject: "Permissions granted or decided",
  action: "One verb, across everything",
};

/** A timestamp, split so the date does not compete with the time. */
function when(at) {
  const raw = String(at || "");
  const [date, rest] = raw.split("T");
  if (!rest) return raw || "—";
  return (
    <>
      {date}
      <Box component="span" sx={{ color: "text.secondary" }}>
        {" "}
        {rest.slice(0, 12)}
      </Box>
    </>
  );
}

const KIND_COLOUR = {
  record: "text.secondary",
  person: "kernel.attention",
  app: "kernel.attention",
  unknown: "kernel.stale",
};

/**
 * What a row touched, chipped by kind.
 *
 * A grant and an object write look nothing alike and reading a list of them
 * without the distinction is reading two logs interleaved. Colour is never
 * the only signal — the kind is a word on the chip — because a permission
 * change rendered as an amber dot does not read at all for a colour-blind
 * operator.
 */
function Subject({ subject }) {
  if (!subject) return "—";
  return (
    <Stack
      direction="row"
      spacing={0.75}
      // Never wrapped. A kernel id is 26 characters and wrapping put the
      // chip, the type and the id on three lines each, which turned fifty
      // rows into a page nobody could scan. `DataTable` scrolls sideways in
      // its own container, so the honest answer at 390px is a row that is
      // wider than the phone and scrolls — not one that is three deep.
      sx={{ alignItems: "center", flexWrap: "nowrap", whiteSpace: "nowrap" }}
    >
      <Chip
        size="small"
        variant="outlined"
        label={subject.kind}
        sx={{
          color: KIND_COLOUR[subject.kind] || "text.secondary",
          borderColor: KIND_COLOUR[subject.kind] || "divider",
        }}
      />
      <Box component="span" sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
        {subject.label}
      </Box>
    </Stack>
  );
}

/**
 * Actor, and who it was acting for. Two facts, never one collapsed.
 *
 * ## The column was a wall of client ids
 *
 * `app:20epk1bm93hi1n56mp98qb094`, fifty rows of it. That string is the thing
 * that actually decides what the write was allowed to be, and it is also
 * unreadable — two rows from the same app look different at a glance and two
 * from different apps look the same.
 *
 * The envelope already carries the answer: `app_type` is the name a person
 * wrote in a manifest, beside the client id the kernel enforces against. So
 * the name is the column and **the id is on hover**, which is the way round
 * that suits reading a log. A connector has no `app_type` — it holds no
 * manifest, so the field is absent rather than missing — and its `actor` is
 * already `connector:<name>`, which is a name.
 *
 * Nothing is inferred. A row with neither shows the actor exactly as it
 * stands, because an actor this screen cannot explain is the one worth
 * looking at.
 */
function Who({ p }) {
  if (!p) return "—";

  const actor = String(p.actor || "");
  const connector = actor.startsWith("connector:")
    ? actor.slice("connector:".length)
    : null;
  const name = p.app_type || connector || actor || "—";
  // Only when it says something the name does not. `connector:xero` under
  // `xero` is the same fact twice.
  const detail = p.app_type && actor ? actor : null;

  return (
    <Stack spacing={0.25}>
      <Tooltip
        title={
          detail
            ? `${detail} — the Cognito client this write was signed for. Permission is decided by that, not by the name.`
            : connector
              ? "A connector, not an app. It holds no manifest and no Cognito client, so there is no app name to show."
              : "No app_type on this row, so this is the actor exactly as the envelope carries it."
        }
      >
        <Box
          component="span"
          sx={{
            borderBottom: "1px dotted",
            borderColor: "divider",
            cursor: "help",
          }}
        >
          {name}
        </Box>
      </Tooltip>
      {p.on_behalf_of ? (
        <Typography variant="caption" color="text.secondary">
          for {p.on_behalf_of}
        </Typography>
      ) : null}
    </Stack>
  );
}

/**
 * Initials, for an avatar that is a label rather than a decoration.
 *
 * `contact-of-record` gives `COR` and `xero` gives `XE`. Hyphens are the only
 * thing it knows about, because every writer name in this kernel is either a
 * manifest `app_type` or a connector name and both are kebab-case by
 * convention — and a rule that guessed at word boundaries would produce a
 * different guess the first time somebody used an underscore.
 */
function initials(name) {
  const parts = String(name).split(/[-_]/).filter(Boolean);
  if (parts.length > 1) return parts.slice(0, 3).map((p) => p[0]).join("").toUpperCase();
  return String(name).slice(0, 2).toUpperCase();
}

/**
 * One writer's share of this page: what they did, and who was watching.
 *
 * ## Two colour systems, on purpose
 *
 * The avatar is the series colour — identity, matching the band in the chart
 * above so the eye can get from one to the other. The bar is `mode`, which is
 * a **state** and therefore semantic: `kernel.unattended` for work with
 * nobody in front of it, the brand for work a person drove, `kernel.stale`
 * for rows written before the field existed.
 *
 * Keeping those two jobs apart is what stops a chart legend from reading as a
 * permission. A writer is not a state; a state is not an identity.
 *
 * ## Why the action chips are counted rather than listed
 *
 * `message.send · 8` is a different fact from `message.send`, and the second
 * one is what a list of verbs gives you. A writer that sent one message and a
 * writer that sent eight hundred look identical in a list and the difference
 * is the whole reason somebody opened this screen.
 */
function WriterCard({ name, colour, kind, detail, total, selected, onSelect }) {
  const share = total ? Math.round((detail.count / total) * 100) : 0;

  return (
    <Paper
      component="button"
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      aria-label={`Narrow to ${name}`}
      variant="outlined"
      sx={{
        p: 2,
        minWidth: 0,
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        font: "inherit",
        color: "inherit",
        textAlign: "left",
        cursor: "pointer",
        backgroundColor: "background.paper",
        borderColor: selected ? "primary.main" : undefined,
        "&:hover": { borderColor: "primary.main" },
      }}
    >
      <Stack direction="row" spacing={1.25} sx={{ alignItems: "center", minWidth: 0 }}>
        <Box
          aria-hidden
          sx={{
            width: 30,
            height: 30,
            borderRadius: 1,
            flexShrink: 0,
            display: "grid",
            placeItems: "center",
            backgroundColor: colour,
            color: "background.paper",
            fontSize: "0.6875rem",
            fontWeight: 600,
            letterSpacing: "0.02em",
          }}
        >
          {initials(name)}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, overflowWrap: "anywhere" }}
          >
            {name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {kind === "connector" ? "connector" : "app"} ·{" "}
            {detail.count.toLocaleString()} of {total} on this page · {share}%
          </Typography>
        </Box>
      </Stack>

      <Box>
        <Proportion
          segments={[
            {
              value: detail.unattended,
              colour: "kernel.unattended",
              label: "no person behind it",
              detail:
                "The kernel stamped mode: unattended — the token carried no username claim, so nobody was in front of this.",
            },
            {
              value: detail.interactive,
              colour: "primary.main",
              label: "a person was there",
              detail:
                "The kernel stamped mode: interactive. Somebody was signed in and the write was made for them.",
            },
            {
              value: detail.unrecorded,
              colour: "kernel.stale",
              label: "no mode recorded",
              detail:
                "Written before LO-55, which is when mode started being stamped. Counted on its own rather than assumed to be either — an absence is not a value.",
            },
          ]}
          total={detail.count}
        />
        {/* The word beside the colour, because a bar that reads as colour
            alone does not read at all for a colour-blind operator — and this
            particular bar is the one carrying the governance fact. */}
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
          {detail.unattended
            ? `${detail.unattended} with nobody there`
            : "every one had a person behind it"}
          {detail.unrecorded ? ` · ${detail.unrecorded} before mode existed` : ""}
        </Typography>
      </Box>

      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          flexWrap: "wrap",
          rowGap: 0.5,
          mt: "auto",
          pt: 1.25,
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        {detail.actions.map(([action, n]) => (
          <Chip
            key={action}
            size="small"
            variant="outlined"
            label={`${action} · ${n}`}
            sx={{ fontFamily: "ui-monospace, monospace" }}
          />
        ))}
      </Stack>
    </Paper>
  );
}

/**
 * When this was read, and whether it is still reading.
 *
 * ## A log with no read time cannot be told from a frozen one
 *
 * Every other number on this screen is about what happened; this one is about
 * the screen itself. Without it, a page that failed to refresh and a quiet
 * afternoon look exactly the same, which is the shape of mistake this whole
 * product is against — two different facts rendered identically.
 *
 * ## What "live" means and does not mean
 *
 * It means: re-read the first page every thirty seconds while this tab is
 * visible. It does not mean a stream — nothing is pushed here, there is no
 * subscription, and a row written a second ago appears when the next read
 * happens and not before. The label says *read* rather than *updated* for
 * that reason: `updated 4s ago` would claim the log was checked continuously
 * and it was not.
 *
 * It stops while you are paging, and says so rather than going quiet.
 */
function Freshness({ readAt, now, live, loading, paging, onToggle, onRefresh }) {
  const seconds = readAt ? Math.max(0, Math.round((now - readAt) / 1000)) : null;
  const ago =
    seconds == null
      ? "not read yet"
      : seconds < 5
        ? "read just now"
        : seconds < 60
          ? `read ${seconds}s ago`
          : seconds < 3600
            ? `read ${Math.round(seconds / 60)}m ago`
            : `read ${Math.round(seconds / 3600)}h ago`;

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
      <Tooltip
        title={
          paging
            ? "Paused while you are paging. A cursor names a position in one particular walk, so re-reading page four would either hand back what is behind you or quietly shift what page four means as rows land at the front."
            : live
              ? "Re-reads the first page every 30 seconds while this tab is visible. Nothing is pushed — a row written a second ago appears at the next read and not before."
              : "Not re-reading. Every read costs, and this screen prints what; turn it back on when you want the page to keep up."
        }
      >
        <Stack direction="row" spacing={0.75} sx={{ alignItems: "center", cursor: "help" }}>
          {/* Colour is never the only signal: the word is the label and the
              dot is the reminder. */}
          <Box
            aria-hidden
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              flexShrink: 0,
              backgroundColor:
                loading
                  ? "primary.main"
                  : paging || !live
                    ? "kernel.stale"
                    : "kernel.permitted",
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {loading ? "reading…" : ago}
            {paging ? " · paused, paging" : live ? "" : " · not live"}
          </Typography>
        </Stack>
      </Tooltip>

      <Button size="small" onClick={onRefresh} disabled={loading} sx={{ minWidth: 0, px: 1 }}>
        Refresh
      </Button>
      <Button
        size="small"
        onClick={onToggle}
        disabled={paging}
        sx={{ minWidth: 0, px: 1 }}
      >
        {live ? "Pause" : "Go live"}
      </Button>
    </Stack>
  );
}

/**
 * How far back to read.
 *
 * ## It is a filter, not a zoom
 *
 * These set `since` on the request, so the kernel reads less and the page
 * below genuinely covers the window named on the button. The alternative —
 * reading the same page and hiding rows — would put a "7 days" label over
 * whatever one read happened to return, which is the same lie as captioning
 * a page-sized sample "activity".
 *
 * `Everything` clears it rather than passing a far-back date, because "no
 * lower bound" and "since 1970" are different requests and only one of them
 * is what somebody means.
 *
 * The buttons are unlit when a date has been typed into `From` by hand. A
 * person who typed one has not chosen a preset, and lighting the button whose
 * range happens to match would claim they did.
 */
function Windows({ value, onChange }) {
  const day = 24 * 3600 * 1000;
  const ago = (days) => new Date(Date.now() - days * day).toISOString().slice(0, 10);
  const options = [
    ["24h", () => ago(1)],
    ["7d", () => ago(7)],
    ["30d", () => ago(30)],
    ["All", () => ""],
  ];

  return (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={value}
      onChange={(_event, next) => {
        if (!next) return;
        const chosen = options.find(([name]) => name === next);
        onChange(next, chosen ? chosen[1]() : "");
      }}
      aria-label="How far back to read"
    >
      {options.map(([name]) => (
        <ToggleButton key={name} value={name}>
          {name}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

/**
 * When the rows on this page were written.
 *
 * ## The one thing on this screen that is genuinely a sequence
 *
 * Almost every number in this product is a count of what exists now, and
 * drawing one of those as a shape would invent a history the kernel does not
 * have. `at` is different: it is a timestamp on every row, the rows are
 * already sorted by it, and *when the work happened* is a real question that
 * a column of fifty timestamps answers very badly.
 *
 * **It is of this page and it says so.** Not the log, not the week — the
 * fifty rows above it, bucketed by day. A chart captioned "activity" over a
 * page-sized sample would be exactly the overstatement this product exists to
 * prevent, in a medium where overstatement is easier.
 *
 * Days with nothing in them are drawn as a hairline rather than as a gap,
 * because a missing bar and a quiet day look identical otherwise and they are
 * different facts.
 */
function ByDay({ items }) {
  if (items.length < 2) return null;

  const days = new Map();
  for (const row of items) {
    const day = String(row.at || "").slice(0, 10);
    if (!day) continue;
    days.set(day, (days.get(day) || 0) + 1);
  }
  if (days.size < 2) return null;

  // Every day between the first and the last, not only the ones with rows —
  // otherwise four writes on Monday and four on Friday draw as two equal bars
  // side by side and read as "two busy days in a row".
  const sorted = [...days.keys()].sort();
  const start = new Date(`${sorted[0]}T00:00:00Z`);
  const end = new Date(`${sorted[sorted.length - 1]}T00:00:00Z`);
  const span = [];
  for (let d = start; d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
    const key = d.toISOString().slice(0, 10);
    span.push([key, days.get(key) || 0]);
    // A page can straddle a long quiet stretch; past a month this stops being
    // a sparkline and starts being a bad bar chart.
    if (span.length > 45) break;
  }

  return (
    <Box sx={{ minWidth: 0, width: { xs: "100%", sm: 260 } }}>
      <Sparkline
        values={span.map(([, n]) => n)}
        labels={span.map(([day]) => day)}
        height={34}
        label={`Writes per day across the ${span.length} days this page covers, from ${span[0][0]} to ${span[span.length - 1][0]}.`}
      />
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", mt: 0.5 }}
      >
        <Typography variant="caption" color="text.secondary">
          {span[0][0]}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          per day, this page
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {span[span.length - 1][0]}
        </Typography>
      </Stack>
    </Box>
  );
}

/**
 * `interactive`, `unattended`, or neither.
 *
 * The third case is a row written before the field existed and it is not
 * "unattended" — an absence is not a value, and guessing here would put back
 * exactly the inference `mode` was added to remove.
 */
function Mode({ mode }) {
  if (!mode) {
    return (
      <Tooltip title="Written before the field existed. Not a claim that nobody was there.">
        <Typography variant="caption" color="text.secondary">
          not recorded
        </Typography>
      </Tooltip>
    );
  }
  return (
    <Chip
      size="small"
      variant="outlined"
      label={mode}
      sx={{
        color: mode === "interactive" ? "kernel.permitted" : "kernel.unattended",
        borderColor:
          mode === "interactive" ? "kernel.permitted" : "kernel.unattended",
      }}
    />
  );
}

/**
 * One audit row, opened.
 *
 * The envelope comes from the kit's `Provenance` — the same component the
 * record pane uses, with `identity` off because an audit row *names* a record
 * rather than being one. Writing a second version of that block here is the
 * drift the kit exists to prevent, and it would have been the easy thing to
 * do: the two are ninety per cent the same and would have diverged on the
 * ten.
 */
function Event({ row, kinds }) {
  const detail = row.detail || {};
  const keys = Object.keys(detail);

  return (
    <Stack spacing={2}>
      <Box>
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ display: "block" }}
        >
          What it touched
        </Typography>
        <Subject subject={row.subject} />
        {kinds?.[row.subject?.kind] ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {kinds[row.subject.kind]}
          </Typography>
        ) : null}
      </Box>

      <Provenance row={row} identity={false} />

      {keys.length ? (
        <Box>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ display: "block" }}
          >
            What this act recorded
          </Typography>
          {/* Whatever the row carries, not a list of the fields written
              today. A grant records the groups before and after; an approval
              records the ceiling it approved and whether it was trimmed. A
              control-plane write that starts recording something new appears
              here with no change to this file. */}
          <Box sx={{ overflowX: "auto", maxWidth: "100%" }}>
            <Table size="small">
              <TableBody>
                {keys.map((key) => (
                  <TableRow key={key}>
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{
                        color: "text.secondary",
                        width: 170,
                        border: 0,
                        verticalAlign: "top",
                      }}
                    >
                      {key}
                    </TableCell>
                    <TableCell sx={{ border: 0 }}>
                      <Box
                        component="span"
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.75rem",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {render(detail[key])}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Box>
      ) : null}

      <Box>
        <Typography variant="caption" color="text.secondary">
          Event <code>{row.event}</code>. This row is append-only: the writing
          role holds no delete and no batch write, and cannot read this table
          at all — which is what stops it finding a row to overwrite. It is a
          credential, not a proof: nothing here yet checks the rows against
          each other.
        </Typography>
      </Box>
    </Stack>
  );
}

function render(value) {
  if (value === null || value === undefined || value === "") return "—";
  if (Array.isArray(value) || typeof value === "object") {
    return JSON.stringify(value, null, 1);
  }
  return String(value);
}
